import { Reveal, Eyebrow, PageHero, PrimaryLink, OutlineLink } from "@/components/shared";
import { IMAGES } from "@/data/content";
import { useLang } from "@/i18n";

const Nalanda = () => {
  const { t } = useLang();

  const phases = [
    ["nal.phase1Year", "nal.phase1Title", "nal.phase1Body"],
    ["nal.phase2Year", "nal.phase2Title", "nal.phase2Body"],
    ["nal.phase3Year", "nal.phase3Title", "nal.phase3Body"],
  ];

  const supportWays = [
    ["nal.support1Title", "nal.support1Body"],
    ["nal.support2Title", "nal.support2Body"],
    ["nal.support3Title", "nal.support3Body"],
  ];

  return (
    <>
      <PageHero
        testId="nalanda-hero"
        eyebrow={t("nal.heroEyebrow")}
        title={t("nal.heroTitle")}
        intro={t("nal.heroIntro")}
      />

      <section className="bg-ivory" id="project" data-testid="nalanda-project-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <Eyebrow>{t("nal.projectEyebrow")}</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl font-semibold text-charcoal">{t("nal.projectHeading")}</h2>
            <p className="mt-6 text-charcoal/80 leading-relaxed">{t("nal.projectP1")}</p>
            <p className="mt-4 text-charcoal/80 leading-relaxed">{t("nal.projectP2")}</p>
            <div className="mt-8" data-testid="nalanda-page-progress">
              <span
                className="inline-flex items-center gap-2 rounded-full bg-saffron/15 border border-saffron/40 px-5 py-2.5 font-sans text-sm font-semibold text-saffrondeep"
                data-testid="nalanda-page-completed-badge"
              >
                <span className="w-2 h-2 rounded-full bg-saffron" aria-hidden="true" />
                {t("nal.progressValue")}
              </span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <img src={IMAGES.outdoor} alt="Monks gathered in prayer outdoors, evoking the spirit of the Nalanda Study Centre" className="rounded-lg w-full object-cover aspect-[4/3]" data-testid="nalanda-project-image" />
          </Reveal>
        </div>
      </section>

      <section className="bg-charcoal text-ivory" id="construction" data-testid="nalanda-construction-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <Eyebrow dark>{t("nal.constructionEyebrow")}</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl font-semibold text-ivory">{t("nal.constructionHeading")}</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {phases.map(([yearKey, titleKey, bodyKey], i) => (
              <Reveal key={yearKey} delay={i * 90}>
                <article className="card-ivory text-charcoal p-7 h-full" data-testid={`nalanda-phase-${i}`}>
                  <p className="font-serif text-3xl font-semibold text-saffron">{t(yearKey)}</p>
                  <h3 className="mt-3 font-serif text-xl font-semibold">{t(titleKey)}</h3>
                  <p className="mt-3 text-[15px] text-charcoal/80 leading-relaxed">{t(bodyKey)}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ivory" id="support" data-testid="nalanda-support-section">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
          <Reveal>
            <Eyebrow>{t("nal.supportEyebrow")}</Eyebrow>
            <h2 className="text-[32px] sm:text-4xl font-semibold text-charcoal">{t("nal.supportHeading")}</h2>
            <p className="mt-4 text-charcoal/80 max-w-2xl">{t("nal.supportIntro")}</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportWays.map(([titleKey, bodyKey], i) => (
              <Reveal key={titleKey} delay={i * 80}>
                <article className="card-ivory p-7 h-full" data-testid={`nalanda-support-option-${i}`}>
                  <h3 className="font-serif text-xl font-semibold text-charcoal">{t(titleKey)}</h3>
                  <p className="mt-3 text-[15px] text-charcoal/80 leading-relaxed">{t(bodyKey)}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-12 flex flex-wrap gap-4">
            <PrimaryLink to="/donate" testId="nalanda-donate-button">{t("nal.donateCta")}</PrimaryLink>
            <OutlineLink to="/contact" testId="nalanda-contact-button">{t("nal.contactCta")}</OutlineLink>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Nalanda;
